<template>
  <div v-if="hasData" :class="{ 'game-chart-wrap-dark': isDarkMode }">
    <h6 v-if="highlightName" class="game-chart-title mb-0">
      <span class="game-chart-title-label">Průběh:</span>
      <span class="game-chart-title-player" :style="{ color: highlightSeriesColor }">{{ highlightName }}</span>
    </h6>
    <div class="game-chart-wrap">
      <Chart
        type="line"
        :data="chartData"
        :options="chartOptions"
        :plugins="chartPlugins"
        :height="420"
      />
    </div>
    <div class="game-legend">
      <button
        v-for="s in series"
        :key="s.label"
        type="button"
        class="game-legend-badge"
        :class="{
          'is-hi': s.label === highlightName && !isPlayerHidden(s.label),
          'is-off': isPlayerHidden(s.label)
        }"
        :style="legendBadgeStyle(s)"
        :title="isPlayerHidden(s.label) ? 'Zobrazit hráče' : 'Skrýt hráče'"
        @click="togglePlayer(s.label)"
      >{{ s.label }}</button>
    </div>
  </div>
  <div
    v-else
    class="text-center py-4"
    :class="isDarkMode ? 'text-slate-400' : 'text-muted'"
  >
    Graf zatím nemá data.
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { Chart as ChartJS, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import Chart from "primevue/chart";

ChartJS.register(...registerables, annotationPlugin);

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);

const theme = computed(() => {
  if (isDarkMode.value) {
    return {
      title: "#FF8A4C",
      tick: "#94A3B8",
      legend: "#CBD5E1",
      grid: "rgba(51, 65, 85, 0.65)",
      startLine: "rgba(148, 163, 184, 0.35)",
      lastBg: "#0F172A",
      lastFg: "#E2E8F0",
      pointBorder: "#0F172A",
      mute: 0.5
    };
  }
  return {
    title: "#334155",
    tick: "#9ca3af",
    legend: "#6b7280",
    grid: "rgba(226, 232, 240, 0.9)",
    startLine: "rgba(148, 163, 184, 0.55)",
    lastBg: "#1E293B",
    lastFg: "#ffffff",
    pointBorder: "#ffffff",
    mute: 0.35
  };
});

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  player: {
    type: String,
    default: ""
  },
  ownPlayer: {
    type: String,
    default: ""
  }
});

const PLAYER_ORANGE = computed(() => (isDarkMode.value ? "#FF8A4C" : "#FF6B21"));
const PLAYER_BLUE = computed(() => (isDarkMode.value ? "#7BA3C9" : "#5B82AC"));
const PLAYER_PALETTE = [
  "#2A9D8F",
  "#C9A227",
  "#E07A5F",
  "#4C9AD3",
  "#7BA05B",
  "#8D99AE",
  "#D4A373",
  "#6A994E",
  "#A67C8A",
  "#5E8B7E"
];

const sameNick = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const asArray = (value) => (Array.isArray(value) ? value : value ? Object.values(value) : []);

const formatMoney = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "0";
  const num = Number(value);
  const hasDecimals = num % 1 !== 0;
  return num.toLocaleString("cs-CZ", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });
};

const formatAxisMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n);
  if (abs >= 1000000) {
    return `${(n / 1000000).toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} mil.`;
  }
  if (abs >= 10000) {
    return `${Math.round(n / 1000).toLocaleString("cs-CZ")} tis.`;
  }
  return n.toLocaleString("cs-CZ", { maximumFractionDigits: 0 });
};

const hexToRgba = (hex, alpha) => {
  const h = String(hex || "#FF5C00").replace("#", "");
  if (h.length !== 6) return `rgba(255, 92, 0, ${alpha})`;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const badgeTextColor = (hex) => {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return "#ffffff";
  const n = parseInt(h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.62 ? "#0F172A" : "#ffffff";
};

const hiddenPlayers = ref(new Set());

const isPlayerHidden = (label) => hiddenPlayers.value.has(label);

const togglePlayer = (label) => {
  const next = new Set(hiddenPlayers.value);
  if (next.has(label)) {
    next.delete(label);
  } else {
    const visible = series.value.filter((s) => s.label !== label && !next.has(s.label));
    if (!visible.length) return;
    next.add(label);
  }
  hiddenPlayers.value = next;
};

const legendBadgeStyle = (s) => {
  const off = isPlayerHidden(s.label);
  const hi = s.label === highlightName.value && !off;
  if (off) {
    return {
      backgroundColor: isDarkMode.value ? "#1E293B" : "#F1F5F9",
      color: isDarkMode.value ? "#64748B" : "#94A3B8",
      border: "1px solid transparent"
    };
  }
  if (hi) {
    return { backgroundColor: s.color, color: badgeTextColor(s.color) };
  }
  return {
    backgroundColor: hexToRgba(s.color, isDarkMode.value ? 0.22 : 0.16),
    color: s.color,
    border: `1px solid ${hexToRgba(s.color, 0.45)}`
  };
};

const cfg = computed(() => {
  const incoming = props.data;
  if (!incoming || typeof incoming !== "object") return null;
  const inner = incoming.Data || incoming;
  return inner.cfg || null;
});

const labels = computed(() => asArray(cfg.value?.labels));

const series = computed(() => {
  const c = cfg.value;
  if (!c) return [];
  const own = String(props.ownPlayer || "").trim();
  const viewed = String(props.player || "").trim();
  return asArray(c.datasets).map((dataset, idx) => {
    let color = PLAYER_PALETTE[idx % PLAYER_PALETTE.length];
    if (own && sameNick(dataset.label, own)) {
      color = PLAYER_ORANGE.value;
    } else if (own && viewed && sameNick(dataset.label, viewed) && !sameNick(viewed, own)) {
      color = PLAYER_BLUE.value;
    } else if (!own && viewed && sameNick(dataset.label, viewed)) {
      color = PLAYER_ORANGE.value;
    }
    return {
      label: dataset.label,
      color,
      data: asArray(dataset.data).map((v) => (v == null || v === "" ? null : Number(v)))
    };
  });
});

const highlightName = computed(() => {
  const names = series.value.map((s) => s.label);
  if (!names.length) return "";
  const q = String(props.player || "").trim().toLowerCase();
  if (q) {
    const match = names.find((n) => String(n).toLowerCase() === q);
    if (match) return match;
  }
  return names[0];
});

const highlightSeries = computed(() =>
  series.value.find((s) => s.label === highlightName.value) || null
);
const highlightSeriesColor = computed(() => highlightSeries.value?.color || PLAYER_ORANGE.value);

const extremes = computed(() => {
  const s = highlightSeries.value;
  const lbs = labels.value;
  if (!s) return null;
  let maxVal = -Infinity;
  let minVal = Infinity;
  let maxIdx = -1;
  let minIdx = -1;
  let lastIdx = -1;
  s.data.forEach((val, idx) => {
    if (val == null || Number.isNaN(val)) return;
    lastIdx = idx;
    if (val > maxVal) {
      maxVal = val;
      maxIdx = idx;
    }
    if (val < minVal) {
      minVal = val;
      minIdx = idx;
    }
  });
  if (lastIdx < 0) return null;
  return {
    startVal: s.data.find((v) => v != null && !Number.isNaN(v)),
    lastIdx,
    lastVal: s.data[lastIdx],
    lastLabel: lbs[lastIdx],
    maxIdx,
    maxVal,
    maxLabel: lbs[maxIdx],
    minIdx,
    minVal,
    minLabel: lbs[minIdx],
    color: s.color
  };
});

const hasData = computed(() => labels.value.length > 0 && series.value.length > 0);

const chartPlugins = [
  {
    id: "gameChartFlushX",
    afterLayout(chart) {
      const area = chart.chartArea;
      const x = chart.scales?.x;
      if (!area || !x) return;
      const right = chart.width - 12;
      if (right > area.right) {
        x.right = right;
        area.right = right;
      }
    }
  }
];

const chartData = computed(() => ({
  labels: labels.value,
  datasets: series.value.map((s) => {
    const isHi = s.label === highlightName.value;
    const lastIdx = extremes.value?.lastIdx ?? -1;
    return {
      label: s.label,
      data: s.data,
      hidden: hiddenPlayers.value.has(s.label),
      borderColor: isHi ? s.color : hexToRgba(s.color, theme.value.mute),
      backgroundColor: isHi ? hexToRgba(s.color, isDarkMode.value ? 0.22 : 0.16) : "transparent",
      borderWidth: isHi ? 3 : 1.5,
      tension: 0.25,
      fill: isHi,
      spanGaps: true,
      pointHoverRadius: 6,
      pointRadius: s.data.map((_, i) => {
        if (!isHi) return 0;
        if (i === lastIdx) return 5;
        if (i === extremes.value?.maxIdx || i === extremes.value?.minIdx) return 4;
        return 2;
      }),
      pointBackgroundColor: s.color,
      pointBorderColor: theme.value.pointBorder,
      pointBorderWidth: isHi ? 2 : 0,
      order: isHi ? 0 : 1
    };
  })
}));

const chartOptions = computed(() => {
  const ext = extremes.value;
  const annotations = {};

  if (ext && ext.startVal != null) {
    annotations.startLine = {
      type: "line",
      yMin: ext.startVal,
      yMax: ext.startVal,
      borderColor: theme.value.startLine,
      borderWidth: 1,
      borderDash: [6, 4]
    };
  }

  if (ext && !hiddenPlayers.value.has(highlightName.value)) {
    const n = labels.value.length;
    const range = Math.max(ext.maxVal - ext.minVal, 1);
    const xShift = (idx) => {
      if (n <= 1) return 0;
      if (idx <= 0) return 52;
      if (idx >= n - 1) return -52;
      return 0;
    };
    const near = (idxA, valA, idxB, valB) => {
      const idxClose = Math.abs(idxA - idxB) <= Math.max(2, Math.round(n * 0.05));
      const valClose = Math.abs(valA - valB) / range < 0.14;
      return { idxClose, valClose, overlap: idxClose && valClose };
    };
    const vsMax = near(ext.maxIdx, ext.maxVal, ext.lastIdx, ext.lastVal);
    const vsMin = near(ext.minIdx, ext.minVal, ext.lastIdx, ext.lastVal);

    const lastLines = [`${highlightName.value}`, `${formatMoney(ext.lastVal)} Kč`];
    if (vsMax.overlap) lastLines.push("na maximu");

    annotations.lastPoint = {
      type: "label",
      xValue: ext.lastLabel,
      yValue: ext.lastVal,
      backgroundColor: theme.value.lastBg,
      borderColor: ext.color,
      borderWidth: 1,
      borderRadius: 6,
      content: lastLines,
      color: theme.value.lastFg,
      font: { family: "Ubuntu Sans, Ubuntu, sans-serif", size: 10, weight: "bold" },
      padding: 6,
      clip: false,
      xAdjust: xShift(ext.lastIdx),
      yAdjust: vsMax.idxClose ? -36 : -24
    };

    if (!vsMax.overlap && ext.maxIdx !== ext.lastIdx) {
      annotations.maxPoint = {
        type: "label",
        xValue: ext.maxLabel,
        yValue: ext.maxVal,
        backgroundColor: "#065F46",
        borderRadius: 6,
        content: [`Max ${formatMoney(ext.maxVal)} Kč`],
        color: "#ffffff",
        font: { family: "Ubuntu Sans, Ubuntu, sans-serif", size: 9, weight: "bold" },
        padding: 5,
        clip: false,
        xAdjust: xShift(ext.maxIdx) + (vsMax.idxClose ? -28 : 0),
        yAdjust: vsMax.idxClose ? 36 : 18
      };
    }

    if (!vsMin.overlap && ext.minIdx !== ext.lastIdx && ext.minIdx !== ext.maxIdx) {
      annotations.minPoint = {
        type: "label",
        xValue: ext.minLabel,
        yValue: ext.minVal,
        backgroundColor: "#9F1239",
        borderRadius: 6,
        content: [`Min ${formatMoney(ext.minVal)} Kč`],
        color: "#ffffff",
        font: { family: "Ubuntu Sans, Ubuntu, sans-serif", size: 9, weight: "bold" },
        padding: 5,
        clip: false,
        xAdjust: xShift(ext.minIdx),
        yAdjust: vsMin.idxClose ? 36 : 20
      };
    }
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    font: {
      family: "Ubuntu Sans, Ubuntu, sans-serif"
    },
    layout: {
      autoPadding: false,
      padding: { top: 6, right: 16, bottom: 0, left: 0 }
    },
    plugins: {
      title: {
        display: false
      },
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(15, 23, 42, 0.94)",
        titleColor: "#ffffff",
        bodyColor: "#E2E8F0",
        padding: 12,
        borderRadius: 8,
        itemSort: (a, b) => {
          const ay = a.parsed?.y;
          const by = b.parsed?.y;
          const aMissing = ay == null || Number.isNaN(Number(ay));
          const bMissing = by == null || Number.isNaN(Number(by));
          if (aMissing && bMissing) return 0;
          if (aMissing) return 1;
          if (bMissing) return -1;
          return Number(by) - Number(ay);
        },
        callbacks: {
          label: (ctx) => {
            const y = ctx.parsed.y;
            if (y == null) return `${ctx.dataset.label}: –`;
            return ` ${ctx.dataset.label}: ${formatMoney(y)} Kč`;
          },
          afterLabel: (ctx) => {
            const data = ctx.dataset.data || [];
            const i = ctx.dataIndex;
            const prev = i > 0 ? Number(data[i - 1]) : null;
            const y = ctx.parsed.y;
            if (prev == null || Number.isNaN(prev) || y == null) return "";
            const diff = y - prev;
            const pct = prev !== 0 ? (diff / prev) * 100 : 0;
            const sign = diff > 0 ? "+" : "";
            return `  ${sign}${formatMoney(diff)} Kč (${sign}${pct.toFixed(2)} %)`;
          }
        }
      },
      annotation: {
        clip: false,
        annotations
      }
    },
    interaction: { intersect: false, mode: "index" },
    scales: {
      y: {
        beginAtZero: false,
        grace: "18%",
        ticks: {
          color: theme.value.tick,
          padding: 4,
          maxTicksLimit: 6,
          font: { size: 10 },
          callback: (value) => formatAxisMoney(value)
        },
        grid: {
          color: theme.value.grid,
          drawBorder: false,
          drawTicks: false,
          lineWidth: 1,
          borderDash: [2, 4]
        }
      },
      x: {
        offset: false,
        ticks: {
          color: theme.value.tick,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
          padding: 0
        },
        afterFit(scale) {
          scale.paddingRight = 10;
          scale.paddingLeft = 0;
        },
        grid: {
          display: false,
          drawBorder: false
        }
      }
    }
  };
});
</script>

<style scoped>
.game-chart-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.4rem;
  padding: 2px 4px 10px;
}

.game-chart-title-label {
  color: #334155;
}

.game-chart-wrap-dark .game-chart-title-label {
  color: #ffffff;
}

.game-chart-title-player {
  font-weight: inherit;
}

.game-chart-wrap {
  position: relative;
  width: 100%;
  height: 396px;
}

.game-chart-wrap :deep(.p-chart) {
  height: 100%;
}

.game-chart-wrap :deep(canvas) {
  width: 100% !important;
  height: 100% !important;
}

.game-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  padding: 10px 4px 0;
}

.game-legend-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  font-family: inherit;
}

.game-legend-badge.is-hi {
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.12);
}

.game-legend-badge.is-off {
  text-decoration: line-through;
  opacity: 0.75;
}
</style>
